import { AppAPIName } from 'src/constants';
import { API } from 'src/utils/AmplifyApiUtils';

import { ApiTags, appAPI } from '.';

// Mirrors backend/lib/resources/studio_credit/web_api.go — the Studio pricing
// catalog rendered for this portal. Prices/lookup keys come from the code
// catalog; credits reflect the portal's pinned grant ladder.
export interface StudioPlanTier {
  tier: number;
  credits: number;
  monthlyCents: number;
  /** Absent for tiers not sold annually (Freemium). */
  annualCents?: number;
  monthlyLookupKey: string;
  annualLookupKey?: string;
}

export type StudioPlanId = 'freemium' | 'starter' | 'professional' | 'advanced';

export interface StudioPlan {
  plan: StudioPlanId;
  tiers: StudioPlanTier[];
}

export interface StudioPlansResponse {
  catalogVersion: string;
  plans: StudioPlan[];
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CreditBalanceResponse —
// the display subset of the workspace's balance row. `creditBalance` and
// `monthlyCreditGrant` are millicredits (1000 = 1 credit); divide before
// showing them. `periodEnd` is the "credits reset on {date}" the Plans page
// renders. The same shape arrives over websocket (entity STUDIO_CREDIT_BALANCE)
// to keep the balance real time — see websocket/creditBalanceHandler.ts.
export interface CreditBalanceResponse {
  creditBalance: number;
  monthlyCreditGrant: number;
  periodStart: string;
  periodEnd: string;
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CreditUsageDay — one
// UTC calendar day of the window. Days with no charges are present with zero, so
// the chart plots a continuous axis without filling gaps itself. All credit
// amounts on these types are millicredits, matching the balance row.
export interface CreditUsageDay {
  date: string;
  creditsConsumed: number;
}

// Mirrors CreditUsageApp. `name`/`icon` are display enrichment and may be
// absent when the app builder can't be resolved; `appId` is always present.
// `deleted` marks spend by an app the workspace no longer has — its name and
// icon still resolve, so it is not the same as an unresolved app.
export interface CreditUsageApp {
  appId: string;
  name?: string;
  icon?: string;
  deleted?: boolean;
  creditsConsumed: number;
}

// Mirrors CreditUsageUser. Names and avatars are resolved client-side from the
// user store rather than duplicated in the response.
export interface CreditUsageUser {
  userId: string;
  creditsConsumed: number;
}

// Mirrors CreditUsageSummaryResponse — the whole requested window aggregated
// three ways. Totals cover the entire window: the ledger is paged server-side,
// so there is no partial subtotal to mistake for a total.
export interface CreditUsageSummaryResponse {
  from: string;
  to: string;
  totalCreditsConsumed: number;
  byDay: CreditUsageDay[];
  byApp: CreditUsageApp[];
  byUser: CreditUsageUser[];
}

/**
 * Window and filters for the usage summary. `from`/`to` are RFC3339 and default
 * to the trailing 30 days; `userId`/`appId` narrow every aggregate, not just the
 * matching breakdown.
 */
export interface CreditUsageSummaryRequest {
  from?: string;
  to?: string;
  userId?: string;
  appId?: string;
}

// Mirrors backend/lib/resources/studio_credit/web_api.go CurrentPlanResponse —
// the portal's current plan resolved from its live Stripe subscription, for
// all plan families. Studio subscriptions carry the parsed selection (plan,
// interval, credits = included + extras); Classic ones carry the accurate
// display name (fixes the empty-name heuristics for `_ent` customs and
// anonymous `price_1…` prices). `family: 'none'` means no billable
// subscription resolved (trial, cancelled, or no Stripe customer yet).
export type CurrentPlanFamily = 'studio' | 'classic' | 'none';

/**
 * The canonical interval strings of the Studio catalog (backend
 * plans.Interval); the Plans page's month/year toggle maps onto these before
 * hitting the plan-change routes.
 */
export type StudioPlanChangeInterval = 'monthly' | 'annual';

// Mirrors backend/lib/resources/studio_credit/plan_change.go
// PendingPlanChangeInfo — a scheduled downgrade waiting for renewal.
export interface PendingPlanChange {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  credits: number;
  effectiveAt: string;
}

export interface CurrentPlanResponse {
  family: CurrentPlanFamily;
  plan?: StudioPlanId;
  /** 'monthly' | 'annual' for Studio; the Stripe interval for Classic. */
  interval?: string;
  credits?: number;
  includedCredits?: number;
  extraCredits?: number;
  planDisplayName?: string;
  renewalDate?: string;
  cancelAtPeriodEnd: boolean;
  pendingChange?: PendingPlanChange;
}

// --- Plan-change DTOs, mirroring plan_change.go ---

export interface StudioPlanChangeRequest {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  /** TOTAL monthly credits of the target selection (included + extras). */
  credits: number;
}

export interface StudioPlanChangeResponse {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  credits: number;
  includedCredits: number;
  extraCredits: number;
}

export interface StudioUpgradeCheckoutRequest extends StudioPlanChangeRequest {
  successUrl: string;
  cancelUrl: string;
}

export interface StudioUpgradeCheckoutResponse {
  url: string;
}

export interface StudioDowngradeSurvey {
  reasons: string[];
  otherReason: string;
}

export interface StudioDowngradeRequest extends StudioPlanChangeRequest {
  survey?: StudioDowngradeSurvey;
}

export interface StudioDowngradeScheduledResponse {
  pendingChange: PendingPlanChange;
}

// Mirrors plan_change.go CreditChangeRequest — the TOTAL monthly credits of
// the target selection. Both directions hit the same route; the backend
// classifies increase vs decrease against the current subscription.
export interface StudioCreditChangeRequest {
  credits: number;
}

// Mirrors plan_change.go CreditChangeResult — an increase applies immediately
// (`credits` is the new total, `pendingChange` absent); a decrease is pinned
// to renewal (`credits` keeps the current total, `pendingChange` carries the
// scheduled one).
export interface StudioCreditChangeResponse {
  plan: StudioPlanId;
  interval: StudioPlanChangeInterval;
  credits: number;
  includedCredits: number;
  extraCredits: number;
  pendingChange?: PendingPlanChange;
}

export interface StudioDowngradeBlocker {
  code: string;
  message: string;
}

// Mirrors plan_change.go PlanChangePreviewRequest/Response — what a proposed
// change would charge today. With Plan set it previews a Studio plan upgrade;
// without, a credits-only change on the current plan.
export interface StudioPlanChangePreviewRequest {
  plan?: StudioPlanId;
  interval?: StudioPlanChangeInterval;
  credits: number;
}

export interface StudioPlanChangePreviewResponse {
  amountDueTodayCents: number;
  currency?: string;
  /** True when the change is give-less: nothing is charged today. */
  scheduledAtRenewal: boolean;
}

/**
 * Stable plan-change error codes the flows branch on, mirroring the
 * portalerr codes in plan_change.go.
 */
export const StudioPlanChangeErrorCode = {
  paymentMethodRequired: 'studio_plan_change_payment_method_required',
  useDowngradeFlow: 'studio_plan_change_use_downgrade_flow',
  useUpgradeFlow: 'studio_plan_change_use_upgrade_flow',
  noChange: 'studio_plan_change_no_change',
  downgradeBlocked: 'studio_plan_downgrade_blocked',
  noPendingDowngrade: 'studio_plan_change_no_pending_downgrade',
  // The backend rejects every self-serve change on an annual subscription;
  // annual support is deferred to POR-21399.
  annualUnsupported: 'studio_plan_change_annual_unsupported',
} as const;

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Plan-change failures arrive either as the raw axios error thrown by the
// Amplify API wrapper (body under `response.data`) or as an already-unwrapped
// RTK error (`data`), so both containers are probed.
const getErrorBody = (error: unknown): Record<string, unknown> | undefined => {
  if (!isObjectRecord(error)) {
    return undefined;
  }
  if (isObjectRecord(error.response) && isObjectRecord(error.response.data)) {
    return error.response.data;
  }
  return isObjectRecord(error.data) ? error.data : undefined;
};

export const getStudioPlanChangeErrorCode = (
  error: unknown,
): string | undefined => {
  const code = getErrorBody(error)?.code;
  return typeof code === 'string' ? code : undefined;
};

/**
 * The structured blocker list of a 409 blocked-downgrade rejection; undefined
 * for any other error.
 */
export const getStudioDowngradeBlockers = (
  error: unknown,
): StudioDowngradeBlocker[] | undefined => {
  if (
    getStudioPlanChangeErrorCode(error) !==
    StudioPlanChangeErrorCode.downgradeBlocked
  ) {
    return undefined;
  }
  const blockers = getErrorBody(error)?.blockers;
  if (!Array.isArray(blockers)) {
    return [];
  }
  return blockers.filter(
    (blocker): blocker is StudioDowngradeBlocker =>
      isObjectRecord(blocker) &&
      typeof blocker.code === 'string' &&
      typeof blocker.message === 'string',
  );
};

export const studioCreditApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getStudioPlans: build.query<StudioPlansResponse, void>({
      queryFn: async () => {
        try {
          const data = await API.get(AppAPIName, '/studio-plans', {});
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.studioPlans],
    }),
    getCurrentPlan: build.query<CurrentPlanResponse, void>({
      queryFn: async () => {
        try {
          const data = await API.get(AppAPIName, '/current-plan', {});
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.currentPlan],
    }),
    getCreditBalance: build.query<CreditBalanceResponse, void>({
      queryFn: async () => {
        try {
          const data = await API.get(AppAPIName, '/credit-balance', {});
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.creditBalance],
    }),
    // The usage-details screen's whole window, aggregated server-side — one
    // request per window rather than a page walk in the browser. Undefined
    // bounds and filters are dropped rather than sent empty, since the handler
    // rejects an unparseable `from`/`to` outright.
    getCreditUsageSummary: build.query<
      CreditUsageSummaryResponse,
      CreditUsageSummaryRequest
    >({
      queryFn: async ({ from, to, userId, appId }) => {
        try {
          const data = await API.get(AppAPIName, '/credit-usage-summary', {
            queryStringParameters: {
              ...(from ? { from } : {}),
              ...(to ? { to } : {}),
              ...(userId ? { userId } : {}),
              ...(appId ? { appId } : {}),
            },
          });
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: [ApiTags.creditUsageSummary],
    }),
    upgradeStudioPlan: build.mutation<
      StudioPlanChangeResponse,
      StudioPlanChangeRequest
    >({
      queryFn: async (body) => {
        try {
          const data = await API.post(
            AppAPIName,
            '/studio-plan-changes/upgrade',
            {
              body,
            },
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      // The credit delta lands via the subscription webhook, so the refetched
      // balance may briefly lag the new plan; the websocket balance push
      // catches it up.
      invalidatesTags: [ApiTags.currentPlan, ApiTags.creditBalance],
    }),
    setupStudioUpgradeCheckout: build.mutation<
      StudioUpgradeCheckoutResponse,
      StudioUpgradeCheckoutRequest
    >({
      queryFn: async (body) => {
        try {
          const data = await API.post(
            AppAPIName,
            '/studio-plan-changes/checkout-setup',
            { body },
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
    }),
    downgradeStudioPlan: build.mutation<
      StudioDowngradeScheduledResponse,
      StudioDowngradeRequest
    >({
      queryFn: async (body) => {
        try {
          const data = await API.post(
            AppAPIName,
            '/studio-plan-changes/downgrade',
            { body },
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [ApiTags.currentPlan],
    }),
    // Both credit-quantity directions route here (the dedicated T7 endpoint);
    // the backend applies an increase immediately and schedules a decrease at
    // renewal.
    changeStudioCredits: build.mutation<
      StudioCreditChangeResponse,
      StudioCreditChangeRequest
    >({
      queryFn: async (body) => {
        try {
          const data = await API.post(
            AppAPIName,
            '/studio-plan-changes/credits',
            { body },
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      // An increase grants the delta via the subscription webhook (balance); a
      // decrease writes a pending change on the current plan. Invalidate both.
      invalidatesTags: [ApiTags.currentPlan, ApiTags.creditBalance],
    }),
    cancelStudioDowngrade: build.mutation<void, void>({
      queryFn: async () => {
        try {
          await API.post(
            AppAPIName,
            '/studio-plan-changes/cancel-downgrade',
            {},
          );
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [ApiTags.currentPlan],
    }),
    // A query despite the POST verb: previews are idempotent reads, and the
    // per-argument cache makes flipping between credit options instant.
    previewStudioPlanChange: build.query<
      StudioPlanChangePreviewResponse,
      StudioPlanChangePreviewRequest
    >({
      queryFn: async (body) => {
        try {
          const data = await API.post(
            AppAPIName,
            '/studio-plan-changes/preview',
            { body },
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
    }),
  }),
});

export const {
  useGetStudioPlansQuery,
  useGetCreditBalanceQuery,
  useGetCreditUsageSummaryQuery,
  useGetCurrentPlanQuery,
  useUpgradeStudioPlanMutation,
  useSetupStudioUpgradeCheckoutMutation,
  useDowngradeStudioPlanMutation,
  useChangeStudioCreditsMutation,
  useCancelStudioDowngradeMutation,
  usePreviewStudioPlanChangeQuery,
} = studioCreditApi;
